/*
 * Capability catalogue — the single frontend source of truth for capability
 * strings. Mirrors the backend catalogue (backend `p1-01`, design doc §3.4)
 * string-for-string: 20 capabilities, signed off with the backend
 * (17 from `p1-01`, plus `partner:view_audit` from PA-AUDIT,
 * `partner:manage_sso` from the partner-managed SSO change, and
 * `content:manage_categories` from PA-CONTENT).
 *
 * UI/route gating reads these values. They are NEVER an access decision on
 * their own — server-side enforcement (backend `p1-04`) is always authoritative.
 *
 * **One list, deliberately.** There was a second, narrower `GRANTABLE_CAPABILITIES`
 * holding back `partner:view_audit` and `content:manage_categories` on the belief
 * that the role write DTO would refuse them. It whitelists `ALL_CAPABILITIES` and
 * always has (both have been in the backend enum since PA-AUDIT and PA-CONTENT), so
 * the only thing the split achieved was a roles editor that could not grant either —
 * no partner could build a role that reads the Audit Log. Anything the backend
 * stores belongs here and is offered; keeping a hand-maintained subset in step with
 * this list is exactly the drift that caused it.
 */

/** Every capability string, in catalogue order. The one place they are declared. */
export const CAPABILITIES = [
  // partner-scoped
  'partner:manage_settings',
  'partner:manage_members',
  'partner:manage_roles',
  'partner:invite',
  'partner:manage_billing',
  'partner:manage_workspaces',
  // Partner-managed SSO. Its own capability rather than folding into
  // `partner:manage_settings` so an Owner can delegate the Entra connection to
  // an IT person without also handing over billing and roles. Owner holds it by
  // way of the full-catalogue bundle; Manager deliberately does not.
  'partner:manage_sso',
  // PA-AUDIT §9.1 — read-only access to the partner Audit Log. Mirrors the
  // backend's `PartnerCapability.ViewAudit`.
  'partner:view_audit',
  // workspace-scoped
  'workspace:manage_members',
  'workspace:manage_settings',
  // content
  'content:view',
  'content:create',
  'content:edit',
  'content:publish',
  'content:approve',
  'content:delete',
  // Taxonomy authority for the Categories screen (PA-CONTENT §3). Deliberately
  // separate from `content:delete`, which is about content *items* — deleting a
  // post should not confer authority over the tree it hangs off.
  //
  // Seeded by the backend on Editor, Manager and Owner but absent here until
  // 2026-09-09, so `isCapability` rejected it and `sanitisePartnerAccess` stripped
  // it out of every session.
  'content:manage_categories',
  // social
  'social:manage_connections',
  // analytics
  'analytics:view',
  'analytics:export',
] as const;

/** A single capability string from the catalogue. */
export type Capability = (typeof CAPABILITIES)[number];

const CAPABILITY_SET: ReadonlySet<string> = new Set(CAPABILITIES);

/** Type guard: is an arbitrary string a known capability? Use when validating API payloads. */
export function isCapability(value: string): value is Capability {
  return CAPABILITY_SET.has(value);
}
